import { useState } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { mockMentorStudents, mockChatMessages, ChatMessage } from '@/data/mentor-mock-data'
import { Search, Send, MoreVertical, ArrowLeft, Check, CheckCheck, Paperclip } from 'lucide-react'

export function MentorChatView() {
    const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null)
    const [messageInput, setMessageInput] = useState('')
    const [searchTerm, setSearchTerm] = useState('')

    // Get list of students for the sidebar
    const students = mockMentorStudents.filter(student =>
        student.name.toLowerCase().includes(searchTerm.toLowerCase())
    )

    // Get messages for the selected student
    // In a real app, this would fetch from an API
    const currentMessages = selectedStudentId
        ? mockChatMessages.filter(m =>
            (m.senderId === selectedStudentId && m.recipientId === 'mentor-001') ||
            (m.senderId === 'mentor-001' && m.recipientId === selectedStudentId)
        ).sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        : []

    const selectedStudent = selectedStudentId
        ? mockMentorStudents.find(s => s.id === selectedStudentId)
        : null

    const handleSendMessage = (e: React.FormEvent) => {
        e.preventDefault()
        if (!messageInput.trim() || !selectedStudentId) return

        // Mock sending message
        const newMessage: ChatMessage = {
            id: `msg-${Date.now()}`,
            senderId: 'mentor-001',
            senderName: 'Dr. Sarah',
            senderRole: 'mentor',
            recipientId: selectedStudentId,
            message: messageInput,
            timestamp: new Date(),
            read: false
        }

        // In a real app, we would update state or refetch
        console.log('Sending message:', newMessage)
        setMessageInput('')
    }

    return (
        <div className="flex h-[calc(100dvh-210px)] lg:h-[calc(100vh-100px)] gap-4 pb-0 lg:pb-0 overflow-hidden">
            {/* Sidebar - Student List */}
            <div className={`w-full md:w-80 flex flex-col gap-4 ${selectedStudentId ? 'hidden md:flex' : 'flex'}`}>
                <GlassmorphicCard className="flex-1 flex flex-col overflow-hidden">
                    <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                        <h2 className="font-bold text-theme-primary mb-4">Messages</h2>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-secondary" />
                            <input
                                type="text"
                                placeholder="Search students..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border-none text-sm focus:ring-2 focus:ring-primary-blue outline-none"
                            />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {students.map(student => (
                            <button
                                key={student.id}
                                onClick={() => setSelectedStudentId(student.id)}
                                className={`w-full p-4 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-800 last:border-0 ${selectedStudentId === student.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                                    }`}
                            >
                                <div className="relative">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                                        {student.name.split(' ').map(n => n[0]).join('')}
                                    </div>
                                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-slate-900 rounded-full"></div>
                                </div>
                                <div className="flex-1 text-left">
                                    <div className="flex justify-between items-start">
                                        <h3 className="font-semibold text-theme-primary text-sm">{student.name}</h3>
                                        <span className="text-[10px] text-theme-secondary">2m</span>
                                    </div>
                                    <p className="text-xs text-theme-secondary truncate max-w-[160px]">
                                        Click to start chatting...
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>
                </GlassmorphicCard>
            </div>

            {/* Chat Window */}
            <div className={`flex-1 flex flex-col ${!selectedStudentId ? 'hidden md:flex' : 'flex'}`}>
                <GlassmorphicCard className="flex-1 flex flex-col overflow-hidden">
                    {selectedStudent ? (
                        <>
                            {/* Chat Header */}
                            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setSelectedStudentId(null)}
                                        className="md:hidden p-2 -ml-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full"
                                    >
                                        <ArrowLeft className="h-5 w-5 text-theme-secondary" />
                                    </button>
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                                        {selectedStudent.name.split(' ').map(n => n[0]).join('')}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-theme-primary">{selectedStudent.name}</h3>
                                        <p className="text-xs text-green-500 flex items-center gap-1">
                                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                                            Online
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-theme-secondary">
                                        <MoreVertical className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Messages Area */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50 dark:bg-slate-900/50">
                                {currentMessages.length > 0 ? (
                                    currentMessages.map((msg) => {
                                        const isMe = msg.senderId === 'mentor-001'
                                        return (
                                            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                                <div className={`max-w-[80%] md:max-w-[60%] rounded-2xl px-4 py-2 ${isMe
                                                    ? 'bg-primary-blue text-white rounded-tr-none'
                                                    : 'bg-white dark:bg-slate-800 text-theme-primary rounded-tl-none shadow-sm'
                                                    }`}>
                                                    <p className="text-sm">{msg.message}</p>
                                                    <div className={`flex items-center gap-1 justify-end mt-1 text-[10px] ${isMe ? 'text-blue-100' : 'text-slate-400'}`}>
                                                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        {isMe && (
                                                            msg.read ? <CheckCheck className="h-3 w-3" /> : <Check className="h-3 w-3" />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-full text-theme-secondary">
                                        <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                                            <Search className="h-8 w-8 text-slate-400" />
                                        </div>
                                        <p>No messages yet</p>
                                        <p className="text-sm">Start a conversation with {selectedStudent.name}</p>
                                    </div>
                                )}
                            </div>

                            {/* Input Area */}
                            <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800">
                                <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="p-3 text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                                    >
                                        <Paperclip className="h-5 w-5" />
                                    </button>
                                    <input
                                        type="text"
                                        placeholder="Type your message..."
                                        value={messageInput}
                                        onChange={(e) => setMessageInput(e.target.value)}
                                        className="flex-1 bg-slate-100 dark:bg-slate-800 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-blue outline-none text-sm"
                                    />
                                    <button
                                        type="submit"
                                        disabled={!messageInput.trim()}
                                        className="p-3 bg-primary-blue text-white rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Send className="h-5 w-5" />
                                    </button>
                                </form>
                            </div>
                        </>
                    ) : (
                        <div className="hidden md:flex flex-col items-center justify-center h-full text-center p-8">
                            <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-6">
                                <Search className="h-10 w-10 text-primary-blue" />
                            </div>
                            <h3 className="text-xl font-bold text-theme-primary mb-2">Select a student to chat</h3>
                            <p className="text-theme-secondary max-w-sm">
                                Choose a student from the sidebar to view history or start a new conversation.
                            </p>
                        </div>
                    )}
                </GlassmorphicCard>
            </div>
        </div >
    )
}
